import { throttle } from 'lodash'
import { CardString } from 'pokersolver'
import { useEffect, useRef, useState } from 'react'
import { router, Router, useRouter } from 'expo-router'

import { GAME_CONFIG, Player } from '@ppwr/poker'

import { ModalContentConfig } from '../../../components/ANewDesign/ModalContent/Content'
import { useStore } from '../../../models'
import { GameModalKinds, GameModalType } from '../../../models/game/game-modals'
import { useJoinCall } from './useJoinCall'
import { events } from '../../../models/analytics/analytics-events'
import { Linking, Platform } from 'react-native'
import { getRouteToLastVisitedTab } from '../../../navigation'

const isDev = !!__DEV__

export const useGameModals = () => {
  const [modalConfig, setModalConfig] = useState<ModalContentConfig | undefined>()
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const router = useRouter()

  const {
    analytics: { setEvent },
    game: {
      myPlayer,
      communityCards,
      bots,
      getFirstGameModal,
      shiftGameModals,
      doBuyForPlayer,
      setHasUserTappedOnAction,
      setHasUserDeclinedToBuy,
      leaveCurrentGame,
      isMyFirstGame
    },
    session: { lastVisitedTab }
  } = useStore()

  const modal = getFirstGameModal()

  const onClose = () => {
    setModalConfig(undefined)
    shiftGameModals()
  }

  const handleLeaveGame = async () => {
    try {
      await leaveCurrentGame()
      // store.game.clearGameModalQueue()
      router.navigate(getRouteToLastVisitedTab(lastVisitedTab))
    } catch (error) {
      console.warn('gotoLobby no-op error', error)
    }
  }

  const doContinuePracticeGame = throttle(async () => {
    try {
      requestAnimationFrame(async () => {
        await Promise.all(
          bots.map((bot) => {
            const botUserId = bot.userId
            return doBuyForPlayer(10000, botUserId)
          })
        )

        onClose()
      })
    } catch (error) {
      console.error(error)
    }
  }, 500)

  const doBuy = throttle(async (value: number) => {
    try {
      setEvent(events.game.add_chips_tapped, { add_chips_response_type: 'yes' })
      onClose()
      await doBuyForPlayer(value)
      setHasUserTappedOnAction(true)
      setEvent(events.game.add_chips_succeeded)
      requestAnimationFrame(async () => {
        setHasUserDeclinedToBuy(false)
      })
    } catch (error) {
      setHasUserTappedOnAction(false)
      console.log('Error while buying chips ', error)
    }
  }, 500)

  const onNoThanks = async () => {
    setEvent(events.game.add_chips_tapped, { add_chips_response_type: 'no' })
    setHasUserDeclinedToBuy(true)
    onClose()
  }

  const onEndPracticeGame = () => {
    handleLeaveGame()
    onClose()
  }

  const onEndThreeCard = () => {
    onClose()
    setEvent(events.threeCard.exited)
    handleLeaveGame()
  }

  const { onJoinAudio, onJoinVideo, onJoinWithAvOff } = useJoinCall({ onClose })

  useEffect(() => {
    if (modal) {
      if (modal.timeToKeepOpen) {
        // @ts-expect-error
        timerRef.current = setTimeout(() => {
          onClose()
        }, modal.timeToKeepOpen * 1000)
      }

      setModalConfig(
        getModalConfig({
          modal,
          onClose,
          onJoinAudio,
          onJoinVideo,
          onJoinWithAvOff,
          onEndPracticeGame,
          onEndThreeCard,
          data: { communityCards, player: myPlayer, isMyFirstGame: Boolean(isMyFirstGame) },
          doBuy,
          doContinuePracticeGame,
          onNoThanks,
          navigation: router.navigate
        })
      )
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal])

  return {
    modalConfig,
    setModalConfig,
    onModalClose: onClose
  }
}
interface GetModalConfigProps {
  modal: GameModalType
  onClose: () => void
  data: { communityCards: CardString[]; player: Player | undefined; isMyFirstGame: boolean }
  onJoinVideo: () => void
  onJoinAudio: () => void
  onJoinWithAvOff: () => void
  onEndPracticeGame: () => void
  onEndThreeCard: () => void
  doBuy: (value: number) => void
  doContinuePracticeGame: () => void
  onNoThanks: () => void
  navigation: Router['navigate']
}
const getModalConfig = ({
  modal,
  onClose,
  onJoinVideo,
  onJoinAudio,
  onJoinWithAvOff,
  onEndPracticeGame,
  onEndThreeCard,
  data: { communityCards, player, isMyFirstGame },
  doBuy,
  doContinuePracticeGame,
  onNoThanks,
  navigation
}: GetModalConfigProps): ModalContentConfig | undefined => {
  switch (modal.kind) {
    case GameModalKinds.handWinnersModal: {
      return {
        kind: modal.kind,
        props: {
          onClose
        }
      }
    }
    case GameModalKinds.allIn:
      return {
        kind: 'allIn',
        props: {
          player,
          onClose
        }
      }
    case GameModalKinds.options:
      if (isMyFirstGame) {
        return
      }
      return {
        kind: 'options',
        props: {
          onClose,
          onJoinWithAvOff,
          onJoinVideo,
          onJoinAudio
        },
        dismissOnBackdrop: isDev
      }
    case GameModalKinds.buy:
      return {
        kind: 'buy',
        props: {
          onClose,
          confirm: { text: 'Yes, continue', onPress: () => doBuy(GAME_CONFIG.DEFAULT_REBUY_AMOUNT_FOR_QUICK_GAME) },
          cancel: { text: 'No, thanks', onPress: onNoThanks }
        },
        dismissOnBackdrop: true
      }
    case GameModalKinds.endPracticeGame:
      return {
        kind: 'endPracticeGame',
        props: {
          onClose,
          confirm: { text: 'Yes', onPress: doContinuePracticeGame },
          cancel: { text: 'No, thanks', onPress: onEndPracticeGame }
        }
      }
    case GameModalKinds.threeCardStart:
      return {
        kind: 'threeCardStart',
        props: {
          onClose
        }
      }
    case GameModalKinds.threeCardLeaveConfirm:
      return {
        kind: 'threeCardLeaveConfirm',
        props: {
          onClose,
          onLeave: onEndThreeCard
        }
      }
    case GameModalKinds.gameDeleted:
      return {
        kind: 'gameDeleted',
        props: {
          onClose: () => console.log('closed'),
          confirm: {
            text: 'OK',
            onPress: () => {
              router.replace('/(root)/(tabs)/play')
              onClose()
            }
          }
        }
      }
    case GameModalKinds.removedByTeacher:
      return {
        kind: 'removedByTeacher',
        props: {
          onClose: () => console.log('closed'),
          confirm: {
            text: 'OK',
            onPress: () => {
              router.replace('/(root)/(tabs)/play')
              onClose()
            }
          }
        }
      }
    case GameModalKinds.appPermission:
      return {
        kind: 'appPermission',
        props: {
          onClose,
          cancel: {
            text: 'Cancel',
            onPress: onClose
          },
          confirm: {
            text: 'OK',
            onPress: () => {
              if (Platform.OS === 'ios') {
                Linking.openURL('app-settings:')
              } else {
                Linking.openSettings()
              }
              onClose()
            }
          }
        },
        dismissOnBackdrop: true
      }
    default:
      console.error(`MODAL ERROR: kind ${modal.kind} was not recognized`)
  }
}
